import { NotificationDescription } from '@/components/notification-description'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ShimmeringText } from '@/components/ui/shimmering-text'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { endPoint } from '@/constant/end-point'
import { NotificationStatus, Role } from '@/enum/global.enum'
import { useActiveOrg } from '@/hooks/use-active-org'
import useAPI from '@/hooks/use-api'
import useApiMutation from '@/hooks/use-api-mutation'
import { useDataFetch } from '@/hooks/use-data-fetch'
import { useDateFormat } from '@/hooks/use-date-format'
import { useMe } from '@/hooks/use-me'
import usePagination from '@/hooks/use-pagination'
import { useSocket } from '@/hooks/use-socket'
import type { INotificationResponse } from '@/interface/notification.interface'
import { cn } from '@/lib/utils'
import { notificationEntityLabel } from '@/utils/notification-entity-label.utils'
import { getNotificationTargetPath } from '@/utils/notification-navigation.utils'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, Calendar, Loader, RefreshCcw } from 'lucide-react'
import * as React from 'react'
import { Link, useLocation, useNavigate } from 'react-router'

export const Notification = () => {
  const { activeOrg } = useActiveOrg()

  if (
    activeOrg?.role === Role.ORG_ADMIN ||
    activeOrg?.role === Role.ORG_AUDITOR ||
    activeOrg?.role === Role.ORG_MANAGER
  )
    return <NotificationContent />

  return null
}

export const NotificationContent = () => {
  const { activeOrg } = useActiveOrg()
  const navigate = useNavigate()
  const { search } = useLocation()
  const queryClient = useQueryClient()
  const { formatDate } = useDateFormat()
  const [pagination, setPagination] = usePagination()
  const { socket, isConnected } = useSocket()
  const { me } = useMe()
  const [open, setOpen] = React.useState(false)

  const { getOne } = useAPI<{
    total: number
    unread: number
    read: number
  }>()

  const { data: notificationCounts } = useQuery({
    queryKey: ['notification-counts'],
    queryFn: () => getOne(`${endPoint.organisation.notification.counts}`),
  })

  const { isLoading, values } = useDataFetch<INotificationResponse>({
    endpoint: endPoint.organisation.notification.getAll,
    pagination: { pagination, setPagination },
  })

  const { updateMutation: markNotificationAsReadMutation } = useApiMutation<{ notificationId: string }>({
    endpoint: endPoint.organisation.notification.markAsRead,
    customQueryKey: ['notification-counts'],
  })

  const { updateMutation: markAllNotificationsAsReadMutation } = useApiMutation({
    endpoint: endPoint.organisation.notification.markAllAsRead,
    customQueryKey: ['notification-counts'],
  })

  // Listen for notification created events via socket
  React.useEffect(() => {
    if (!socket || !isConnected || !me?.id) return

    const handleNotificationCreated = (payload: {
      targetUserId?: string | null
      organisationId: string
      timestamp: string
    }) => {
      if (!activeOrg?.id || payload.organisationId !== activeOrg.id) return

      const isUserSpecificNotification = payload.targetUserId === me.id
      const isOrganisationWideNotification = !payload.targetUserId

      // Process notifications targeted to the current user OR to everyone in the organisation
      if (isUserSpecificNotification || isOrganisationWideNotification) {
        // Invalidate queries to refresh notification counts and list
        void queryClient.invalidateQueries({ queryKey: ['notification-counts'], exact: false })
        void queryClient.invalidateQueries({
          queryKey: [endPoint.organisation.notification.getAll],
          exact: false,
        })
      }
    }

    socket.on('notification:created', handleNotificationCreated)

    return () => {
      socket.off('notification:created', handleNotificationCreated)
    }
  }, [socket, isConnected, me?.id, activeOrg?.id, queryClient])

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant='outline' size='icon' className='relative'>
          <Bell className='h-[1.2rem] w-[1.2rem]' />
          <span className='sr-only'>Toggle theme</span>
          {notificationCounts?.data?.unread && notificationCounts?.data?.unread > 0 ? (
            <span className='text-[9px] h-5 w-5 rounded-full absolute -top-2 -right-2 bg-green-500 text-white   border flex items-center justify-center'>
              {notificationCounts?.data?.unread > 99 ? '99+' : notificationCounts?.data?.unread}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-86' align='end'>
        <div className='flex justify-between gap-2 items-center'>
          <h4 className='flex gap-1 items-center text-sm font-medium text-muted-foreground px-2 py-2'>
            <Bell className='h-4 w-4 ' />
            Notifications
          </h4>
          <div className='pr-4 flex gap-1 items-center'>
            <Tooltip>
              <TooltipTrigger>
                <RefreshCcw
                  className={cn(
                    'h-3.5 w-3.5 cursor-pointer text-muted-foreground hover:text-accent-foreground',
                    isLoading && 'animate-spin'
                  )}
                  aria-label='Refresh notifications'
                  onClick={() => {
                    void queryClient.invalidateQueries({ queryKey: ['notification-counts'], exact: false })
                    void queryClient.invalidateQueries({
                      queryKey: [endPoint.organisation.notification.getAll],
                      exact: false,
                    })
                  }}
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>Refresh notifications</p>
              </TooltipContent>
            </Tooltip>

            {notificationCounts?.data?.unread && notificationCounts?.data?.unread > 0 ? (
              <Button
                size={'sm'}
                className='text-xs h-7'
                variant={'ghost'}
                onClick={async () => {
                  if (!notificationCounts?.data?.unread || notificationCounts?.data?.unread <= 0) return
                  void markAllNotificationsAsReadMutation.mutateAsync({})
                  void queryClient.invalidateQueries({ queryKey: ['notification-counts'], exact: false })
                  void queryClient.invalidateQueries({
                    queryKey: [endPoint.organisation.notification.getAll],
                    exact: false,
                  })
                }}
              >
                Read All
              </Button>
            ) : null}
          </div>
        </div>
        <hr />
        <div className='py-1' />
        {isLoading ? (
          <DropdownMenuItem>
            <div className='flex items-center gap-2 '>
              <Loader className='h-[1.2rem] w-[1.2rem] animate-spin' />
              <ShimmeringText text='Loading Notifications....' className='text-sm' />
            </div>
          </DropdownMenuItem>
        ) : (
          <div className='max-h-90 min-h-90 overflow-y-auto'>
            {values && values.length > 0 ? (
              values?.map((item, idx) => {
                return (
                  <DropdownMenuItem
                    className={cn(
                      item.userNotifications[0]?.status === NotificationStatus.READ && 'bg-accent',
                      'cursor-pointer',
                      idx > 0 && 'my-1',
                      idx === values.length - 1 && 'mb-0'
                    )}
                    onClick={async () => {
                      const path = getNotificationTargetPath(item, search)
                      if (path) navigate(path)
                      setOpen(false)

                      if (item.userNotifications[0]?.status === NotificationStatus.READ) return
                      await markNotificationAsReadMutation.mutateAsync({ notificationId: item.id })
                      void queryClient.invalidateQueries({
                        queryKey: [endPoint.organisation.notification.getAll],
                      })
                    }}
                  >
                    <div className='flex flex-col'>
                      <span className='text-xs font-medium'>{item.title}</span>
                      <small>
                        {typeof item?.description === 'string' ? (
                          // Strip metadata inside square brackets
                          <NotificationDescription
                            text={item.description?.substring(0, 300).replace(/\s*\[[^\]]*\]/g, '')}
                          />
                        ) : (
                          ''
                        )}
                        {item?.description?.length && item?.description?.length > 300 && '...'}
                      </small>
                      <span className='text-xs text-muted-foreground flex items-center gap-1'>
                        <Calendar className='h-[12px] w-[12px] size-4 -mb-[0.5px]' />
                        <span className='text-xs text-muted-foreground pt-1 [&_svg]:text-[8px]'>
                          {formatDate(item.createdAt)}
                        </span>
                        <span className='text-[11px] capitalize bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md border'>
                          {notificationEntityLabel(item.entityType ?? '')}
                        </span>
                      </span>
                    </div>
                  </DropdownMenuItem>
                )
              })
            ) : (
              <div className='flex justify-center items-center max-h-90 min-h-90'>
                <span className='text-muted-foreground text-sm'>No notifications found</span>
              </div>
            )}
          </div>
        )}

        <div className='py-1' />
        <hr />
        <div className='pt-1' />

        <DropdownMenuItem asChild className='flex justify-center'>
          <Link className='text-xs text-muted-foreground' to='/notifications' onClick={() => setOpen(false)}>
            View all notifications
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
